import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import apiService from '../../services/api.service';
import { cn } from '../../lib/utils';

interface Props {
  open: boolean;
  eventId: string | null;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

export function ImportTeamsDialog({ open, eventId, onOpenChange, onImported }: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => { setFile(null); setError(''); onOpenChange(false); };

  const accept = (f: File) => {
    if (f.type.includes('spreadsheet') || f.name.endsWith('.xlsx') || f.name.endsWith('.csv')) {
      setFile(f);
      setError('');
    } else {
      setError(t('teams.importUnsupportedFormat'));
    }
  };

  const submit = async () => {
    if (!file || !eventId) return;
    setBusy(true);
    setError('');
    try {
      await apiService.importTeams(file, eventId);
      onImported();
      close();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await apiService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_equipes.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('errors.generic'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('teams.importTeams')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="text-sm text-muted-foreground">
            <p className="mb-2 font-semibold text-foreground">{t('teams.importFormat')}</p>
            <p className="mb-2">{t('teams.importOneRowPerParticipant')}</p>
            <ol className="list-decimal space-y-0.5 pl-5">
              <li>{t('teams.teamName')}</li>
              <li>{t('common.firstName')}</li>
              <li>{t('common.lastName')}</li>
              <li>{t('common.email')}</li>
              <li>{t('common.phone')}</li>
              <li>{t('teams.category')}</li>
            </ol>
          </div>

          <Button variant="outline" onClick={downloadTemplate}>
            <Download size={16} />
            {t('teams.downloadTemplate')}
          </Button>

          <Separator />

          <button
            type="button"
            className={cn(
              'w-full rounded-lg border-2 border-dashed p-8 text-center transition-colors',
              dragOver ? 'border-white/40 bg-white/[0.08]' : 'border-muted-foreground/30 hover:border-white/40',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) accept(f);
            }}
            onClick={() => document.getElementById('teams-import-input')?.click()}
          >
            <Upload size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="mb-1 font-medium text-foreground">{file ? file.name : t('teams.importDropzone')}</p>
            <p className="text-sm text-muted-foreground">{t('teams.importDropzoneClick')}</p>
          </button>
          <input
            id="teams-import-input"
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => e.target.files?.[0] && accept(e.target.files[0])}
            className="hidden"
          />

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={!file || busy}>
            {busy && <Loader2 className="animate-spin" />}
            {t('common.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
